import type { Customer } from '@clockwise/clients/customers';
import type { Department } from '@clockwise/clients/departments';
import type { ExpenseCatalog } from '@clockwise/clients/expense-catalogs';
import type { User } from '@clockwise/clients/users';
import { customersApi, departmentsApi, expenseCatalogsApi, usersApi } from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

/**
 * The list/detail fetch + query-key contract for an entity picker. Single
 * source of truth shared by the single-select autocompletes
 * (`customer-autocomplete`, `user-autocomplete`, `department-autocomplete`)
 * AND the multi-select `EntityMultiSelect`, so both compute identical query
 * keys and share the React Query cache — they can never drift apart.
 */
export interface EntityPickerConfig<T> {
  /** What this picker CHOOSES, singular and capitalised — "Customer",
   *  "Employee". Names the control for a screen reader, which then hears the
   *  field and its current value ("Customer: Acme Robotics") rather than the
   *  placeholder. Lives on the config rather than on each call site so every
   *  picker over the same entity announces itself the same way. */
  entityLabel?: string;
  fetchList: (opts: { q?: string; limit: number }) => Promise<{ data: T[] }>;
  fetchOne: (id: string) => Promise<{ data: T }>;
  listKey: (opts: { q?: string; limit: number }) => readonly unknown[];
  detailKey: (id: string) => readonly unknown[];
  getId: (item: T) => string;
  getLabel: (item: T) => string;
  /** Haystack for client-side ('preload') filtering — should include every
   *  field the server `q=` search matches (e.g. name AND qbId/email), so the
   *  in-memory filter behaves like the lazy server path. Defaults to
   *  `getLabel`; set it whenever the label alone doesn't cover what users type
   *  to find a row. Unused in lazy/always mode (the server does the matching). */
  searchText?: (item: T) => string;
  /** Default page size for lazy fetches over this entity, so the page size is
   *  a property of the catalog rather than something every picker has to
   *  remember. The hook's own default (20) is a sensible page for a typeahead
   *  but a poor *list*: a filter dropdown opened without typing then shows 20
   *  rows of a 500-row catalog and reads as the whole thing, which is how a
   *  live client came to look absent from the timesheet report. Set it to the
   *  endpoint's server-side ceiling — asking for more is just clamped, and
   *  anything the page still misses is called out by the truncation footer.
   *  A `lazyLimit` prop on the picker overrides this. */
  lazyLimit?: number;
  /** The endpoint's hard `limit` ceiling, when it is below the hook's default
   *  of 500. Multi-select pickers pad the request by the number of selected
   *  rows, so a `lazyLimit` already sitting at the endpoint's maximum would
   *  otherwise ask for one row too many and 400 the moment something is
   *  selected. Keep this in step with the domain's `maxLimit` on the API side.
   *  Set it whenever you set `lazyLimit` — the pair is the contract. */
  maxLimit?: number;
}

/** Ceiling on the customers and users list endpoints — see their `maxLimit`
 *  in apps/api/src/core/{customers,users}/domain.ts. Their catalogs are the
 *  ones big enough for a short page to hide a row someone is looking for, so
 *  the pickers ask for the whole thing. */
const BIG_CATALOG_PAGE = 500;

/** Ceiling on the departments list endpoint — lower than every other picker's,
 *  and low enough that the page size and the ceiling are the same number. */
const DEPARTMENT_PAGE = 200;

export const customerPickerConfig: EntityPickerConfig<Customer> = {
  entityLabel: 'Customer',
  fetchList: ({ q, limit }) =>
    customersApi.list({
      q,
      status: 'active',
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  fetchOne: (id) => customersApi.get(id),
  listKey: ({ q, limit }) =>
    queryKeys.customers.list({
      q,
      status: 'active',
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  detailKey: (id) => queryKeys.customers.detail(id),
  getId: (c) => c.id,
  getLabel: (c) => c.name,
  // Mirror the backend `q` search (name + QB id) so client-side filtering
  // finds a customer by its QB id, not just its name.
  searchText: (c) => `${c.name} ${c.qbId ?? ''}`,
  lazyLimit: BIG_CATALOG_PAGE,
  maxLimit: BIG_CATALOG_PAGE,
};

/**
 * Expense-catalog items, for the pass-through picker.
 *
 * `active: true` only. A deactivated item means "stop offering this", and the
 * API refuses to seed a new customer line from one — a picker that listed it
 * would be offering a choice that always 400s. Existing lines that already
 * reference a retired item resolve through `fetchOne`, which is unfiltered, so
 * they still render their item's name.
 */
export const expenseCatalogPickerConfig: EntityPickerConfig<ExpenseCatalog> = {
  entityLabel: 'Expense catalog',
  fetchList: ({ q, limit }) =>
    expenseCatalogsApi.list({
      q,
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  fetchOne: (id) => expenseCatalogsApi.get(id),
  listKey: ({ q, limit }) =>
    queryKeys.expenseCatalogs.list({
      q,
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  detailKey: (id) => queryKeys.expenseCatalogs.detail(id),
  getId: (e) => e.id,
  getLabel: (e) => e.name,
  // Mirror the backend `q` search (name + description) so client-side
  // filtering finds an item by what it charges for, not just its SKU-ish name.
  searchText: (e) => `${e.name} ${e.description}`,
};

/** Display name: preferred "First Last", else email. Reused by the user row. */
export const userLabel = (u: User) => `${u.firstName} ${u.lastName}`.trim() || u.email;

export const userPickerConfig: EntityPickerConfig<User> = {
  entityLabel: 'Employee',
  fetchList: ({ q, limit }) =>
    usersApi.list({
      q,
      active: true,
      sortField: 'firstName',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  fetchOne: (id) => usersApi.get(id),
  listKey: ({ q, limit }) =>
    queryKeys.users.list({
      q,
      active: true,
      sortField: 'firstName',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  detailKey: (id) => queryKeys.users.detail(id),
  getId: (u) => u.id,
  getLabel: userLabel,
  // Mirror the backend `q` search (name + email) so client-side filtering
  // finds an employee by email — and so near-identical names stay reachable.
  searchText: (u) => `${userLabel(u)} ${u.email}`,
  lazyLimit: BIG_CATALOG_PAGE,
  maxLimit: BIG_CATALOG_PAGE,
};

export const departmentPickerConfig: EntityPickerConfig<Department> = {
  entityLabel: 'Department',
  fetchList: ({ q, limit }) =>
    departmentsApi.list({
      q,
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  fetchOne: (id) => departmentsApi.get(id),
  listKey: ({ q, limit }) =>
    queryKeys.departments.list({
      q,
      active: true,
      sortField: 'name',
      sortDirection: 'asc',
      limit,
      offset: 0,
    }),
  detailKey: (id) => queryKeys.departments.detail(id),
  getId: (d) => d.id,
  getLabel: (d) => d.name,
  // The departments endpoint caps `limit` at 200 in both its validator and its
  // domain config, and a tenant's department list is nowhere near that — so
  // one page is the whole catalog and the footer never has cause to appear.
  // maxLimit must be stated: at 200 the page size IS the ceiling, so without
  // it the multi-select's excludeCount padding would request 201 and 400.
  lazyLimit: DEPARTMENT_PAGE,
  maxLimit: DEPARTMENT_PAGE,
};
